import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, RotateCcw, Trash2 } from "lucide-react";
import { api, errMsg, useApi } from "../lib/api";
import { fmtTime } from "../lib/labels";
import { PageHeader } from "../components/Layout";
import { ConfirmDialog, EmptyRow, Field, FormError } from "../components/Common";
import { Button } from "../components/ui/button";
import { BlipPicker, BlipSwatch } from "../components/BlipColor";
import { resolveBlip, useBlipColors } from "../lib/blip";
import { OrgMapPreview } from "../components/OrgMapPreview";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const OrgDialog = ({ org, onClose, onSaved }) => {
  const isNew = !org?.id;
  const [form, setForm] = useState({ name: "", label: "", points: 0, color: "#94a3b8", blip_color: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setForm({ name: org?.name || "", label: org?.label || "", points: org?.points ?? 0, color: org?.color || "#94a3b8", blip_color: org?.blip_color ?? "" });
    setError("");
  }, [org]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const blipColors = useBlipColors();
  const blip = resolveBlip(form.blip_color, form.color, blipColors);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.label.trim()) return setError("Preencha o nome do gang e o nome visível.");
    if (!/^#[0-9a-fA-F]{6}$/.test(form.color)) return setError("Cor: formato inválido (ex: #A855F7)");
    const blip = String(form.blip_color).trim();
    if (blip !== "" && !(/^\d+$/.test(blip) && Number(blip) <= 85)) return setError("Cor do blip: número inteiro entre 0 e 85 (ou vazio para automático)");
    const payload = { name: form.name, label: form.label, color: form.color, blip_color: blip === "" ? null : Number(blip) };
    if (!isNew) {
      if (form.points === "" || !Number.isInteger(Number(form.points))) return setError("Pontos: deve ser um número inteiro");
      payload.points = Number(form.points);
    }
    setBusy(true);
    try {
      if (isNew) await api.post("/admin/orgs", payload);
      else await api.put(`/admin/orgs/${org.id}`, payload);
      toast.success(isNew ? "Organização criada." : "Organização atualizada.");
      onSaved();
    } catch (err) {
      setError(errMsg(err, "Erro ao guardar organização."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!org} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto border-slate-800 bg-slate-950" data-testid="org-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">{isNew ? "Nova organização" : "Editar organização"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field label="Nome do gang (QBCore)" value={form.name} onChange={set("name")} testid="org-name-input" placeholder="ex: ballas" />
          <Field label="Nome visível" value={form.label} onChange={set("label")} testid="org-label-input" placeholder="ex: Ballas" />
          <div className="flex items-end gap-3">
            <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(form.color) ? form.color : "#94a3b8"} onChange={set("color")}
              className="h-10 w-14 cursor-pointer border border-slate-700 bg-slate-900 p-1" data-testid="org-color-picker" aria-label="Escolher cor" />
            <Field label="Cor (hex)" value={form.color} onChange={set("color")} testid="org-color-input" placeholder="#A855F7" className="flex-1" />
          </div>
          <div>
            <div className="flex items-end gap-2">
              <Field label="Cor do blip GTA (0–85, opcional)" type="number" value={form.blip_color} onChange={set("blip_color")} testid="org-blip-color-input" placeholder="Automático" className="flex-1" />
              <BlipPicker colors={blipColors} value={form.blip_color} onPick={(id) => setForm({ ...form, blip_color: id })} />
            </div>
            <div className="mt-2 flex min-h-6 items-center gap-3">
              <BlipSwatch {...blip} large testid="org-blip-preview" />
              {!blip.color && String(form.blip_color).trim() !== "" && <span className="font-mono text-[11px] text-rose-400" data-testid="org-blip-invalid">ID inválido</span>}
            </div>
            <p className="mt-1 font-mono text-[11px] text-slate-500">Vazio = cor de blip mais próxima da cor hex (a mesma que o jogo usa).</p>
          </div>
          <OrgMapPreview hex={form.color} blip={blip} />
          {!isNew && <Field label="Pontos" type="number" value={form.points} onChange={set("points")} testid="org-points-input" />}
          <FormError message={error} testid="org-form-error" />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} className="border-slate-700 bg-transparent" data-testid="org-cancel-button">Cancelar</Button>
            <Button type="submit" disabled={busy} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="org-save-button">Guardar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const cooldownUntil = (org, settings) => {
  if (!org.last_declared_at || !settings) return null;
  const until = Date.parse(org.last_declared_at) + settings.cooldown_minutes * 60000;
  return until > Date.now() ? until : null;
};

const OrgRow = ({ org, settings, blipColors, onEdit, onDelete, onReset }) => {
  const cd = cooldownUntil(org, settings);
  return (
    <tr className="row-hover border-b border-slate-800/70 hover:bg-slate-900/60" data-testid={`org-row-${org.name}`}>
      <td className="px-4 py-3 font-mono text-sm text-emerald-300">{org.name}</td>
      <td className="px-4 py-3 font-medium"><span className="mr-2 inline-block h-3 w-3 rounded-full align-middle" style={{ background: org.color }} data-testid={`org-color-${org.name}`} />{org.label}
</td>
      <td className="px-4 py-3"><BlipSwatch {...resolveBlip(org.blip_color, org.color, blipColors)} testid={`org-blip-${org.name}`} /></td>
      <td className="px-4 py-3 text-right font-mono font-bold" data-testid={`org-points-${org.name}`}>{org.points}</td>
      <td className="px-4 py-3 text-right font-mono text-sm text-slate-400">{org.wins} / {org.losses} / {org.kills}</td>
      <td className="px-4 py-3 text-right font-mono text-sm">{org.zones_held}</td>
      <td className="px-4 py-3" data-testid={`org-cooldown-${org.name}`}>
        {cd ? <span className="font-mono text-xs text-amber-400">Cooldown até {fmtTime(cd)}</span> : <span className="font-mono text-xs text-slate-500">Disponível</span>}
      </td>
      <td className="px-4 py-3">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" title="Repor cooldown" disabled={!cd} onClick={() => onReset(org)} data-testid={`org-reset-cooldown-${org.name}`}><RotateCcw className="h-4 w-4 text-amber-400" /></Button>
          <Button size="icon" variant="ghost" title="Editar" onClick={() => onEdit(org)} data-testid={`org-edit-${org.name}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" title="Eliminar" onClick={() => onDelete(org)} data-testid={`org-delete-${org.name}`}><Trash2 className="h-4 w-4 text-rose-400" /></Button>
        </div>
      </td>
    </tr>
  );
};

export default function OrgsPage() {
  const { data: orgs, reload } = useApi("/admin/orgs");
  const { data: settings } = useApi("/admin/settings");
  const blipColors = useBlipColors();
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const run = async (fn, ok) => {
    try {
      await fn();
      toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Gangs QBCore" title="Organizações">
        <Button onClick={() => setEditing({})} className="bg-emerald-500 text-slate-950 hover:bg-emerald-400" data-testid="create-org-button"><Plus className="mr-1 h-4 w-4" />Nova organização</Button>
      </PageHeader>
      <div className="panel fade-up overflow-x-auto">
        <table className="w-full text-left" data-testid="orgs-table">
          <thead className="border-b border-slate-800 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <tr><th className="px-4 py-3">Gang</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Blip GTA</th><th className="px-4 py-3 text-right">Pontos</th><th className="px-4 py-3 text-right">V / D / Kills</th><th className="px-4 py-3 text-right">Zonas</th><th className="px-4 py-3">Cooldown</th><th className="px-4 py-3" /></tr>
          </thead>
          <tbody>
            {orgs && orgs.length === 0 && <EmptyRow cols={8} text="Sem organizações." testid="orgs-empty" />}
            {(orgs || []).map((o) => (
              <OrgRow key={o.id} org={o} settings={settings} blipColors={blipColors} onEdit={setEditing} onDelete={setDeleting}
                onReset={(org) => run(() => api.post(`/admin/orgs/${org.id}/reset-cooldown`), "Cooldown reposto.")} />
            ))}
          </tbody>
        </table>
      </div>
      <OrgDialog org={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
      <ConfirmDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)} testid="org-delete-dialog"
        title="Eliminar organização?" confirmLabel="Eliminar"
        description={`${deleting?.label || ""} será eliminada e as zonas que domina ficarão sem dono.`}
        onConfirm={() => { run(() => api.delete(`/admin/orgs/${deleting.id}`), "Organização eliminada."); setDeleting(null); }} />
    </div>
  );
}
